import { useState } from 'react';
import { X, Check, User, Mail, Phone, Building, Briefcase, MapPin, ArrowRight, ArrowLeft } from 'lucide-react';
import { useTranslation } from '../../context/LanguageContext';

/**
 * Modal for comparing contact data with existing people data
 * Allows user to choose which value to keep for each field
 */
const ContactComparisonModal = ({ isOpen, onClose, formData, peopleData, onConfirm }) => {
    const { language } = useTranslation();

    // Fields to compare
    const fields = [
        { key: 'firstName', label: language === 'fr' ? 'Prénom' : 'First Name', icon: User },
        { key: 'lastName', label: language === 'fr' ? 'Nom' : 'Last Name', icon: User },
        { key: 'email', label: 'Email', icon: Mail },
        { key: 'phone', label: language === 'fr' ? 'Téléphone' : 'Phone', icon: Phone },
        { key: 'gsm', label: 'GSM', icon: Phone },
        { key: 'company', label: language === 'fr' ? 'Société' : 'Company', icon: Building },
        { key: 'position', label: language === 'fr' ? 'Fonction' : 'Position', icon: Briefcase },
        { key: 'googleAddress', label: language === 'fr' ? 'Adresse' : 'Address', icon: MapPin }
    ];

    // Initialize selected values - prefer form data if not empty, else people data
    const [selectedValues, setSelectedValues] = useState(() => {
        const initial = {};
        fields.forEach(field => {
            const formValue = formData?.[field.key] || '';
            const peopleValue = peopleData?.[field.key] || peopleData?.[field.key.toLowerCase()] || '';
            // If form has value, use it; otherwise use people value
            initial[field.key] = formValue || peopleValue;
            // Track which source is selected: 'form' or 'people'
            initial[`${field.key}_source`] = formValue ? 'form' : 'people';
        });
        return initial;
    });

    // Handle value selection
    const selectValue = (fieldKey, source) => {
        const value = source === 'form'
            ? (formData?.[fieldKey] || '')
            : (peopleData?.[fieldKey] || peopleData?.[fieldKey.toLowerCase()] || '');

        setSelectedValues(prev => ({
            ...prev,
            [fieldKey]: value,
            [`${fieldKey}_source`]: source
        }));
    };

    // Handle confirm
    const handleConfirm = () => {
        // Build merged data
        const mergedData = {};
        fields.forEach(field => {
            mergedData[field.key] = selectedValues[field.key];
        });

        // Include peopleUuid from the found people
        mergedData.peopleuuid = peopleData?.peopleUuid || peopleData?.peopleuuid || peopleData?.PEOPLEUUID || '';

        onConfirm(mergedData);
    };

    // Use all people data
    const useAllPeopleData = () => {
        const newValues = {};
        fields.forEach(field => {
            const peopleValue = peopleData?.[field.key] || peopleData?.[field.key.toLowerCase()] || '';
            newValues[field.key] = peopleValue;
            newValues[`${field.key}_source`] = 'people';
        });
        setSelectedValues(newValues);
    };

    // Use all form data
    const useAllFormData = () => {
        const newValues = {};
        fields.forEach(field => {
            const formValue = formData?.[field.key] || '';
            newValues[field.key] = formValue;
            newValues[`${field.key}_source`] = 'form';
        });
        setSelectedValues(newValues);
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50" onClick={onClose}>
            <div
                className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden"
                onClick={e => e.stopPropagation()}
            >
                {/* Header */}
                <div className="bg-gradient-to-r from-amber-500 to-orange-500 p-4 flex items-center justify-between">
                    <div>
                        <h2 className="text-white text-lg font-semibold">
                            {language === 'fr' ? 'Contact existant trouvé' : 'Existing Contact Found'}
                        </h2>
                        <p className="text-white/80 text-sm">
                            {language === 'fr'
                                ? 'Choisissez les données à conserver pour chaque champ'
                                : 'Choose which data to keep for each field'}
                        </p>
                    </div>
                    <button onClick={onClose} className="text-white hover:bg-white/20 p-1 rounded-full transition">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Quick actions */}
                <div className="p-3 border-b border-gray-200 bg-gray-50 flex gap-2 justify-center">
                    <button
                        onClick={useAllFormData}
                        className="px-3 py-1.5 text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition flex items-center gap-1"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        {language === 'fr' ? 'Tout vos données' : 'All your data'}
                    </button>
                    <button
                        onClick={useAllPeopleData}
                        className="px-3 py-1.5 text-sm bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition flex items-center gap-1"
                    >
                        {language === 'fr' ? 'Tout données existantes' : 'All existing data'}
                        <ArrowRight className="w-4 h-4" />
                    </button>
                </div>

                {/* Comparison table */}
                <div className="p-4 overflow-y-auto max-h-[50vh]">
                    {/* Header row */}
                    <div className="grid grid-cols-[140px_1fr_1fr] gap-2 mb-2 px-2">
                        <div className="text-sm font-medium text-gray-500">
                            {language === 'fr' ? 'Champ' : 'Field'}
                        </div>
                        <div className="text-sm font-medium text-blue-600 text-center">
                            {language === 'fr' ? 'Vos données' : 'Your data'}
                        </div>
                        <div className="text-sm font-medium text-green-600 text-center">
                            {language === 'fr' ? 'Données existantes' : 'Existing data'}
                        </div>
                    </div>

                    {/* Field rows */}
                    {fields.map(field => {
                        const formValue = formData?.[field.key] || '';
                        const peopleValue = peopleData?.[field.key] || peopleData?.[field.key.toLowerCase()] || '';
                        const selectedSource = selectedValues[`${field.key}_source`];
                        const Icon = field.icon;

                        // Skip if both are empty
                        if (!formValue && !peopleValue) return null;

                        return (
                            <div key={field.key} className="grid grid-cols-[140px_1fr_1fr] gap-2 mb-2">
                                {/* Field label */}
                                <div className="flex items-center gap-2 text-sm text-gray-700 px-2">
                                    <Icon className="w-4 h-4 text-gray-400" />
                                    {field.label}
                                </div>

                                {/* Form value */}
                                <button
                                    onClick={() => selectValue(field.key, 'form')}
                                    disabled={!formValue}
                                    className={`p-2 rounded-lg text-sm text-left transition ${
                                        selectedSource === 'form'
                                            ? 'bg-blue-100 border-2 border-blue-500 text-blue-800'
                                            : formValue
                                                ? 'bg-gray-50 border border-gray-200 hover:border-blue-300 text-gray-700'
                                                : 'bg-gray-50 border border-gray-200 text-gray-400 cursor-not-allowed'
                                    }`}
                                >
                                    <div className="flex items-center justify-between">
                                        <span className="truncate">{formValue || '-'}</span>
                                        {selectedSource === 'form' && formValue && (
                                            <Check className="w-4 h-4 text-blue-600 flex-shrink-0" />
                                        )}
                                    </div>
                                </button>

                                {/* People value */}
                                <button
                                    onClick={() => selectValue(field.key, 'people')}
                                    disabled={!peopleValue}
                                    className={`p-2 rounded-lg text-sm text-left transition ${
                                        selectedSource === 'people'
                                            ? 'bg-green-100 border-2 border-green-500 text-green-800'
                                            : peopleValue
                                                ? 'bg-gray-50 border border-gray-200 hover:border-green-300 text-gray-700'
                                                : 'bg-gray-50 border border-gray-200 text-gray-400 cursor-not-allowed'
                                    }`}
                                >
                                    <div className="flex items-center justify-between">
                                        <span className="truncate">{peopleValue || '-'}</span>
                                        {selectedSource === 'people' && peopleValue && (
                                            <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                                        )}
                                    </div>
                                </button>
                            </div>
                        );
                    })}
                </div>

                {/* Info banner */}
                <div className="px-4 py-2 bg-amber-50 border-t border-amber-200">
                    <p className="text-sm text-amber-700">
                        {language === 'fr'
                            ? 'Ce contact sera lié au profil public existant et marqué comme "PUBLIC".'
                            : 'This contact will be linked to the existing public profile and marked as "PUBLIC".'}
                    </p>
                </div>

                {/* Footer */}
                <div className="p-4 border-t border-gray-200 bg-gray-50 flex gap-3">
                    <button
                        onClick={onClose}
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition"
                    >
                        {language === 'fr' ? 'Annuler' : 'Cancel'}
                    </button>
                    <button
                        onClick={handleConfirm}
                        className="flex-1 px-4 py-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-lg font-medium hover:opacity-90 transition flex items-center justify-center gap-2"
                    >
                        <Check className="w-4 h-4" />
                        {language === 'fr' ? 'Confirmer et créer' : 'Confirm and create'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ContactComparisonModal;
